import { Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { AiAssistantI18nModule } from '../../../../assets/i18n/ai-assistant-i18n.module';
import { AiKnowledgeService } from '@galaxy/ai-knowledge';
import {
  KnowledgeSource,
  KnowledgeSourceConfig,
  KnowledgeSourceConfigCustomDataConfig,
  KnowledgeSourceConfigInterface,
  KnowledgeSourceConfigWebsiteScrapeConfig,
} from '@vendasta/embeddings';
import { TranslateModule } from '@ngx-translate/core';
import { MatListItemIcon } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { NgTemplateOutlet } from '@angular/common';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { KnowledgeForm } from '../../../../core/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { modeOptions } from '../knowledge-card-constants';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

@Component({
  selector: 'ai-add-knowledge-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    GalaxyBadgeModule,
    AiAssistantI18nModule,
    GalaxyLoadingSpinnerModule,
    GalaxyInfiniteScrollTriggerModule,
    TranslateModule,
    MatListItemIcon,
    MatMenuModule,
    NgTemplateOutlet,
    CdkTextareaAutosize,
    MatInput,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatOption,
    MatSelect,
  ],
  templateUrl: './add-knowledge-dialog.component.html',
  styleUrls: ['./add-knowledge-dialog.component.scss', '../../../ai-assistant-configuration/ai-assistant-shared.scss'],
})
export class AddKnowledgeDialogComponent {
  private dialogRef = inject(MatDialogRef<AddKnowledgeDialogComponent>);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly snackbarService = inject(SnackbarService);

  protected readonly knowledgeSources: KnowledgeSource[] = inject(MAT_DIALOG_DATA).knowledgeSources || [];
  protected creatingTextKnowledge = signal(false);
  protected creatingWebKnowledge = signal(false);

  newKnowledgeForm = computed(() => {
    const creatingTextKnowledge = this.creatingTextKnowledge();
    const creatingWebKnowledge = this.creatingWebKnowledge();
    if (creatingTextKnowledge) {
      return new KnowledgeForm(
        new KnowledgeSource({
          config: new KnowledgeSourceConfig({ customDataConfig: new KnowledgeSourceConfigCustomDataConfig() }),
        }),
      );
    } else if (creatingWebKnowledge) {
      return new KnowledgeForm(
        new KnowledgeSource({
          config: new KnowledgeSourceConfig({ websiteScrapeConfig: new KnowledgeSourceConfigWebsiteScrapeConfig() }),
        }),
      );
    }
    return new KnowledgeForm();
  });

  createTextKnowledge() {
    this.creatingTextKnowledge.set(true);
  }

  createWebKnowledge() {
    this.creatingWebKnowledge.set(true);
  }

  createKnowledge() {
    try {
      const form = this.newKnowledgeForm();
      const name = form.controls.name.value;

      if (!form.valid) {
        form.markAllAsTouched();
        this.snackbarService.openErrorSnack('AI_ASSISTANT.KNOWLEDGE_ACCESS.UNABLE_TO_SAVE');
        return;
      }

      const config: KnowledgeSourceConfigInterface = this.creatingTextKnowledge()
        ? {
            customDataConfig: {
              text: form.controls.content.value,
            },
          }
        : {
            websiteScrapeConfig: {
              url: form.controls.url.value,
              crawlMode: form.controls.crawlMode.value,
            },
          };
      this.knowledgeService.createKnowledgeSource(name, config).then(async (id) => {
        const knowledgeSource = await this.knowledgeService.getKnowledgeSource(
          id,
          this.knowledgeService.buildNamespaceInterface(),
        );
        this.snackbarService.openSuccessSnack('AI_ASSISTANT.KNOWLEDGE_ACCESS.SUCCESS_CREATING_KNOWLEDGE');
        this.chooseKnowledgeSource(knowledgeSource);
      });
    } catch (err) {
      console.error(err);
      this.snackbarService.openErrorSnack('AI_ASSISTANT.KNOWLEDGE_ACCESS.ERROR_CREATING_KNOWLEDGE');
    }
  }

  cancelCreate() {
    this.creatingTextKnowledge.set(false);
    this.creatingWebKnowledge.set(false);
  }

  getKnowledgePreview(source: KnowledgeSource) {
    return this.knowledgeService.getKnowledgePreview(source);
  }

  chooseKnowledgeSource(knowledgeSource: KnowledgeSource) {
    this.dialogRef.close({
      knowledge: knowledgeSource,
    });
  }

  protected readonly modeOptions = modeOptions;
}
